/**
 * STT設定管理画面
 * - STT誤認識修正辞書管理
 * - 無音判定閾値設定
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSttCorrectionDictionary,
  createSttCorrectionDictionaryEntry,
  updateSttCorrectionDictionaryEntry,
  deleteSttCorrectionDictionaryEntry,
  getSttSettings,
  updateSttSetting,
} from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Loader2, Mic, Save, List, Search } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { getPhraseList, createPhrase, deletePhrase } from '../../lib/admin-api';

interface SttCorrectionDictionaryEntry {
  id: number;
  misrecognized_text: string;
  correct_text: string;
  description?: string;
  priority: number;
  created: string;
  updated: string;
}

const PAGE_SIZE = 20;

export default function SttSettings() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<SttCorrectionDictionaryEntry | null>(null);
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({
    misrecognized_text: '',
    correct_text: '',
    description: '',
    priority: 0,
  });
  const [silenceThresholdDb, setSilenceThresholdDb] = useState<string>('-35');

  // マイクテスト用
  const [isMicTesting, setIsMicTesting] = useState(false);
  const [currentDb, setCurrentDb] = useState<number>(-100);
  const [currentRms, setCurrentRms] = useState<number>(0);
  const [silenceStatus, setSilenceStatus] = useState<'sound' | 'silence' | 'idle'>('idle');
  const micStreamRef = useRef<MediaStream | null>(null);
  const micContextRef = useRef<AudioContext | null>(null);
  const micTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Phrase List
  const [newPhrase, setNewPhrase] = useState('');
  const [phrasePage, setPhrasePage] = useState(1);
  const [phraseSearch, setPhraseSearch] = useState('');
  const { data: phraseListData, isLoading: isLoadingPhrases } = useQuery({
    queryKey: ['admin', 'phrase-list'],
    queryFn: getPhraseList,
  });

  const createPhraseMutation = useMutation({
    mutationFn: (phrase: string) => createPhrase(phrase),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'phrase-list'] });
      setNewPhrase('');
      toast({ title: '成功', description: 'フレーズを追加しました' });
    },
    onError: (error: Error) => {
      toast({ title: 'エラー', description: error.message, variant: 'destructive' });
    },
  });

  const deletePhraseMutation = useMutation({
    mutationFn: (id: number) => deletePhrase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'phrase-list'] });
      toast({ title: '成功', description: 'フレーズを削除しました' });
    },
    onError: (error: Error) => {
      toast({ title: 'エラー', description: error.message, variant: 'destructive' });
    },
  });

  const phraseEntries = (phraseListData?.entries ?? []) as { id: number; phrase: string; is_active: boolean }[];
  const filteredPhraseEntries = phraseSearch.trim()
    ? phraseEntries.filter((e) => e.phrase.toLowerCase().includes(phraseSearch.trim().toLowerCase()))
    : phraseEntries;

  // STT誤認識修正辞書一覧取得
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'stt-correction-dictionary'],
    queryFn: async () => {
      const result = await getSttCorrectionDictionary();
      console.log('STT誤認識修正辞書APIレスポンス:', result);
      return result;
    },
  });

  // STT設定取得
  const { data: sttSettingsData } = useQuery({
    queryKey: ['admin', 'stt-settings'],
    queryFn: () => getSttSettings(),
  });

  // 設定値の初期値を設定
  useEffect(() => {
    if (sttSettingsData?.settings) {
      const silenceSetting = sttSettingsData.settings.find(
        (s: any) => s.setting_key === 'silence_threshold_db'
      );
      if (silenceSetting) {
        setSilenceThresholdDb(silenceSetting.setting_value);
      }
    }
  }, [sttSettingsData]);

  const createMutation = useMutation({
    mutationFn: createSttCorrectionDictionaryEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stt-correction-dictionary'] });
      setIsCreateDialogOpen(false);
      setFormData({ misrecognized_text: '', correct_text: '', description: '', priority: 0 });
      toast({
        title: '成功',
        description: 'STT誤認識修正辞書エントリを作成しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'STT誤認識修正辞書エントリの作成に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateSttCorrectionDictionaryEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stt-correction-dictionary'] });
      setIsEditDialogOpen(false);
      setSelectedEntry(null);
      setFormData({ misrecognized_text: '', correct_text: '', description: '', priority: 0 });
      toast({
        title: '成功',
        description: 'STT誤認識修正辞書エントリを更新しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'STT誤認識修正辞書エントリの更新に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSttCorrectionDictionaryEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stt-correction-dictionary'] });
      setIsDeleteDialogOpen(false);
      setSelectedEntry(null);
      toast({
        title: '成功',
        description: 'STT誤認識修正辞書エントリを削除しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'STT誤認識修正辞書エントリの削除に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const updateSilenceThresholdMutation = useMutation({
    mutationFn: (threshold: number) => updateSttSetting('silence_threshold_db', { value: threshold, description: '無音判定閾値(dB)' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stt-settings'] });
      toast({ title: '成功', description: '無音判定閾値を更新しました' });
    },
    onError: (error: Error) => {
      toast({ title: 'エラー', description: error.message || '無音判定閾値の更新に失敗しました', variant: 'destructive' });
    },
  });

  const handleSaveSilenceThreshold = () => {
    const threshold = parseFloat(silenceThresholdDb);
    if (isNaN(threshold) || threshold > 0 || threshold < -60) {
      toast({ title: 'エラー', description: '無音閾値は-60から0の間の数値である必要があります', variant: 'destructive' });
      return;
    }
    updateSilenceThresholdMutation.mutate(threshold);
  };

  const stopMicTest = useCallback(() => {
    if (micTimerRef.current) {
      clearInterval(micTimerRef.current);
      micTimerRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }
    if (micContextRef.current) {
      micContextRef.current.close().catch(() => {});
      micContextRef.current = null;
    }
    setIsMicTesting(false);
    setSilenceStatus('idle');
    setCurrentDb(-100);
    setCurrentRms(0);
  }, []);

  const startMicTest = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { noiseSuppression: true, echoCancellation: true, autoGainControl: true },
      });
      micStreamRef.current = stream;

      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      micContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.fftSize);
      setIsMicTesting(true);

      micTimerRef.current = setInterval(() => {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const v = (dataArray[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        const db = rms > 0.001 ? 20 * Math.log10(rms) : -100;
        setCurrentDb(Math.round(db * 10) / 10);
        setCurrentRms(Math.round(rms * 10000) / 10000);

        const threshold = parseFloat(silenceThresholdDb) || -35;
        const isAboveRms = rms > 0.01;
        const isAboveDb = db > threshold;
        setSilenceStatus(isAboveRms && isAboveDb ? 'sound' : 'silence');
      }, 100);
    } catch (err) {
      toast({ title: 'エラー', description: 'マイクへのアクセスに失敗しました', variant: 'destructive' });
    }
  }, [silenceThresholdDb, toast]);

  // コンポーネントアンマウント時にクリーンアップ
  useEffect(() => {
    return () => { stopMicTest(); };
  }, [stopMicTest]);

  const handleCreate = () => {
    if (!formData.misrecognized_text || !formData.correct_text) {
      toast({
        title: 'エラー',
        description: '誤認識テキストと正しいテキストは必須です',
        variant: 'destructive',
      });
      return;
    }

    createMutation.mutate({
      misrecognized_text: formData.misrecognized_text,
      correct_text: formData.correct_text,
      description: formData.description || undefined,
      priority: formData.priority || 0,
    });
  };

  const handleEdit = (entry: SttCorrectionDictionaryEntry) => {
    setSelectedEntry(entry);
    setFormData({
      misrecognized_text: entry.misrecognized_text,
      correct_text: entry.correct_text,
      description: entry.description || '',
      priority: entry.priority,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedEntry) return;

    if (!formData.misrecognized_text || !formData.correct_text) {
      toast({
        title: 'エラー',
        description: '誤認識テキストと正しいテキストは必須です',
        variant: 'destructive',
      });
      return;
    }

    updateMutation.mutate({
      id: selectedEntry.id,
      data: {
        misrecognized_text: formData.misrecognized_text,
        correct_text: formData.correct_text,
        description: formData.description || undefined,
        priority: formData.priority || 0,
      },
    });
  };

  const handleDelete = (entry: SttCorrectionDictionaryEntry) => {
    setSelectedEntry(entry);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedEntry) {
      deleteMutation.mutate(selectedEntry.id);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">STT設定</h1>
          <p className="text-base text-gray-500 mt-2">音声認識（STT）の設定と誤認識修正辞書とPhrase List管理</p>
        </div>

        {/* 無音判定閾値設定 + マイクテスト (1 枚のカード内・縦線区切り・1:2) */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:divide-x lg:divide-gray-200">
            {/* 無音判定閾値設定 (2/5) */}
            <div className="lg:col-span-2 lg:pr-6">
              <div className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Mic className="w-5 h-5" />
                無音判定閾値設定
              </div>
              <Label htmlFor="silence-threshold" className="text-base font-bold text-gray-700">無音閾値（-60 ～ 0 dB）</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  id="silence-threshold"
                  type="number"
                  min="-60"
                  max="0"
                  step="1"
                  value={silenceThresholdDb}
                  onChange={(e) => setSilenceThresholdDb(e.target.value)}
                  className="max-w-xs h-11 rounded-xl border-gray-200 bg-white"
                />
                <Button
                  onClick={handleSaveSilenceThreshold}
                  disabled={updateSilenceThresholdMutation.isPending}
                  className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm"
                >
                  {updateSilenceThresholdMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      保存
                    </>
                  )}
                </Button>
              </div>
              <p className="text-base text-gray-500 mt-2">
                録音中にこの閾値以下の音量が2秒間続くと、自動的に録音を停止します。
                <br />
                教室など騒がしい環境では値を上げる（例: -25dB）と安定しやすくなります。
                <br />
                推奨値: -40 ～ -25（デフォルト: -35）
              </p>
            </div>

            {/* マイクテスト (3/5) */}
            <div className="lg:col-span-3 lg:pl-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Mic className="w-5 h-5" />
                  マイクテスト
                </div>
                <Button
                  variant="outline"
                  onClick={isMicTesting ? stopMicTest : startMicTest}
                  className={`h-11 w-40 rounded-lg justify-center text-base font-bold ${
                    isMicTesting
                      ? 'border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Mic className="w-4 h-4 mr-1.5" />
                  {isMicTesting ? 'テスト停止' : 'テスト開始'}
                </Button>
              </div>
              {isMicTesting ? (
                <div className="space-y-2 mt-6">
                  <div className="flex items-center gap-3">
                    <span className="text-base w-20 text-right font-mono tabular-nums font-bold text-gray-900 shrink-0">{currentDb.toFixed(1)} dB</span>
                    <div className="flex-1 relative">
                      {/* バー本体 + 音量塗り (上下角丸で塗りをクリップ) */}
                      <div className="h-6 rounded bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full rounded transition-all duration-100 ${
                            silenceStatus === 'sound' ? 'bg-green-500' : 'bg-yellow-400'
                          }`}
                          style={{ width: `${Math.max(0, Math.min(100, (currentDb + 60) / 60 * 100))}%` }}
                        />
                      </div>
                      {/* 閾値ライン (バーから上下にはみ出す) */}
                      <div
                        className="absolute -top-2 -bottom-2 w-0.5 bg-red-500 z-10"
                        style={{ left: `${Math.max(0, Math.min(100, ((parseFloat(silenceThresholdDb) || -35) + 60) / 60 * 100))}%` }}
                      />
                    </div>
                    <span className={`text-base w-12 font-bold ${
                      silenceStatus === 'sound' ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {silenceStatus === 'sound' ? '音声' : '無音'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-base text-gray-500">
                    <span>-60dB</span>
                    <span className="flex-1 text-center text-red-500">
                      赤線 = 閾値 ({silenceThresholdDb}dB)
                    </span>
                    <span>0dB</span>
                  </div>
                  <p className="text-base text-gray-500">
                    RMS: {currentRms} | 緑バーが赤線を超えている間は「音声」と判定されます
                  </p>
                </div>
              ) : (
                <p className="text-base text-gray-500">
                  「テスト開始」を押すとマイクの音量を可視化して、無音判定の閾値が適切かを確認できます。
                </p>
              )}
            </div>
          </div>
        </div>

        {/* STT誤認識修正辞書管理 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <div className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Mic className="w-5 h-5" />
              STT誤認識修正辞書管理
            </div>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
                  <Plus className="w-4 h-4 mr-1.5" />
                  新規作成
                </Button>
              </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>新規STT誤認識修正辞書エントリ作成</DialogTitle>
                    <DialogDescription>新しい誤認識修正エントリを追加します</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="create-misrecognized-text">誤認識テキスト（必須）</Label>
                      <Input
                        id="create-misrecognized-text"
                        value={formData.misrecognized_text}
                        onChange={(e) => setFormData({ ...formData, misrecognized_text: e.target.value })}
                        placeholder="例: 配車"
                      />
                      <p className="text-sm text-gray-500 mt-1">STTで誤って認識されるテキスト</p>
                    </div>
                    <div>
                      <Label htmlFor="create-correct-text">正しいテキスト（必須）</Label>
                      <Input
                        id="create-correct-text"
                        value={formData.correct_text}
                        onChange={(e) => setFormData({ ...formData, correct_text: e.target.value })}
                        placeholder="例: 歯医者"
                      />
                      <p className="text-sm text-gray-500 mt-1">修正後の正しいテキスト</p>
                    </div>
                    <div>
                      <Label htmlFor="create-priority">優先度</Label>
                      <Input
                        id="create-priority"
                        type="number"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                      />
                      <p className="text-sm text-gray-500 mt-1">高い値ほど優先的に適用されます（デフォルト: 0）</p>
                    </div>
                    <div>
                      <Label htmlFor="create-description">説明（任意）</Label>
                      <Textarea
                        id="create-description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="例: 「歯医者」が「配車」と誤認識される場合の修正"
                        rows={3}
                      />
                    </div>
                  </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setIsCreateDialogOpen(false);
                    setFormData({ misrecognized_text: '', correct_text: '', description: '', priority: 0 });
                  }}>
                    キャンセル
                  </Button>
                  <Button onClick={handleCreate} disabled={createMutation.isPending}>
                    {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '作成'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました: {error instanceof Error ? error.message : String(error)}</div>
          ) : !data?.entries || data.entries.length === 0 ? (
            <div className="text-center py-8 text-base text-gray-500">
              データがありません。新規作成ボタンからエントリを追加してください。
            </div>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 hover:bg-transparent">
                  <TableHead className="text-base font-bold text-gray-700">ID</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">誤認識テキスト</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">正しいテキスト</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">優先度</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">説明</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">作成日時</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.entries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((entry: SttCorrectionDictionaryEntry) => (
                  <TableRow key={entry.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{entry.id}</TableCell>
                    <TableCell className="py-4 text-base font-bold text-red-600">{entry.misrecognized_text}</TableCell>
                    <TableCell className="py-4 text-base font-bold text-green-600">{entry.correct_text}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums text-center">{entry.priority}</TableCell>
                    <TableCell className="py-4 text-base text-gray-700">{entry.description || '-'}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums text-center">
                      {new Date(entry.created).toLocaleString('ja-JP')}
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          type="button"
                          onClick={() => handleEdit(entry)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                          aria-label="編集"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(entry)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          aria-label="削除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.entries.length > PAGE_SIZE && (
              <div className="flex justify-between items-center mt-5 pt-4">
                <div className="text-base text-gray-700">
                  全 {data.entries.length} 件中 {((page - 1) * PAGE_SIZE + 1)} - {Math.min(page * PAGE_SIZE, data.entries.length)} 件を表示
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="h-9 rounded-lg border-gray-200"
                  >
                    前へ
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page * PAGE_SIZE >= data.entries.length}
                    className="h-9 rounded-lg border-gray-200"
                  >
                    次へ
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </div>

        {/* 編集ダイアログ */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>STT誤認識修正辞書エントリ編集</DialogTitle>
              <DialogDescription>STT誤認識修正辞書エントリを編集します</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-misrecognized-text">誤認識テキスト（必須）</Label>
                <Input
                  id="edit-misrecognized-text"
                  value={formData.misrecognized_text}
                  onChange={(e) => setFormData({ ...formData, misrecognized_text: e.target.value })}
                  placeholder="例: 配車"
                />
                <p className="text-sm text-gray-500 mt-1">STTで誤って認識されるテキスト</p>
              </div>
              <div>
                <Label htmlFor="edit-correct-text">正しいテキスト（必須）</Label>
                <Input
                  id="edit-correct-text"
                  value={formData.correct_text}
                  onChange={(e) => setFormData({ ...formData, correct_text: e.target.value })}
                  placeholder="例: 歯医者"
                />
                <p className="text-sm text-gray-500 mt-1">修正後の正しいテキスト</p>
              </div>
              <div>
                <Label htmlFor="edit-priority">優先度</Label>
                <Input
                  id="edit-priority"
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
                <p className="text-sm text-gray-500 mt-1">高い値ほど優先的に適用されます</p>
              </div>
              <div>
                <Label htmlFor="edit-description">説明（任意）</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="例: 「歯医者」が「配車」と誤認識される場合の修正"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsEditDialogOpen(false);
                setSelectedEntry(null);
                setFormData({ misrecognized_text: '', correct_text: '', description: '', priority: 0 });
              }}>
                キャンセル
              </Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '更新'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>STT誤認識修正辞書エントリを削除しますか？</AlertDialogTitle>
              <AlertDialogDescription>
                この操作は取り消せません。STT誤認識修正辞書エントリID「{selectedEntry?.id}」（{selectedEntry?.misrecognized_text} → {selectedEntry?.correct_text}）を削除しますか？
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Phrase List (左:追加フォーム / 右:辞書テーブル + ページネーション) */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:divide-x lg:divide-gray-200">
            {/* 左: 新しいフレーズを追加 (2/5) */}
            <div className="lg:col-span-2 lg:pr-6">
              <div className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <List className="w-5 h-5" />
                新しいフレーズを追加
              </div>
              <p className="text-base text-gray-500 mt-1">
                Azure Speech SDKのSTT認識補助用フレーズリスト（最大500件）。
              </p>
              <div className="flex gap-2 mt-4">
                <Input
                  placeholder="新しいフレーズを入力"
                  value={newPhrase}
                  onChange={(e) => setNewPhrase(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newPhrase.trim()) {
                      createPhraseMutation.mutate(newPhrase.trim());
                    }
                  }}
                  className="flex-1 h-11 rounded-xl border-gray-200 bg-white"
                />
                <Button
                  onClick={() => newPhrase.trim() && createPhraseMutation.mutate(newPhrase.trim())}
                  disabled={!newPhrase.trim() || createPhraseMutation.isPending}
                  className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm shrink-0"
                >
                  {createPhraseMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-1" />
                      追加
                    </>
                  )}
                </Button>
              </div>

              {/* 辞書検索 */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  辞書検索
                </div>
                <p className="text-base text-gray-500 mt-1">
                  右側のフレーズ辞書を絞り込めます。
                </p>
                <div className="relative mt-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    placeholder="フレーズを検索"
                    value={phraseSearch}
                    onChange={(e) => {
                      setPhraseSearch(e.target.value);
                      setPhrasePage(1);
                    }}
                    className="h-11 rounded-xl border-gray-200 bg-white pl-9"
                  />
                </div>
              </div>
            </div>

            {/* 右: フレーズ辞書テーブル + ページネーション (3/5) */}
            <div className="lg:col-span-3 lg:pl-6">
              <div className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-4">
                <List className="w-5 h-5" />
                フレーズ辞書
                {phraseEntries.length > 0 && (
                  <span className="ml-2 text-base font-normal text-gray-500">
                    （{phraseEntries.length}件{phraseEntries.length > 500 ? ' / 上限500件' : ''}）
                  </span>
                )}
              </div>
              {isLoadingPhrases ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
                </div>
              ) : phraseEntries.length === 0 ? (
                <div className="text-center py-8 text-base text-gray-500">
                  フレーズがありません。左の入力欄から追加してください。
                </div>
              ) : filteredPhraseEntries.length === 0 ? (
                <div className="text-center py-8 text-base text-gray-500">
                  「{phraseSearch}」に一致するフレーズはありません。
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-gray-200 hover:bg-transparent">
                        <TableHead className="text-base font-bold text-gray-700">#</TableHead>
                        <TableHead className="text-base font-bold text-gray-700">フレーズ</TableHead>
                        <TableHead className="text-base font-bold text-gray-700 w-16 text-center">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPhraseEntries
                        .slice((phrasePage - 1) * PAGE_SIZE, phrasePage * PAGE_SIZE)
                        .map((entry, i) => (
                          <TableRow key={entry.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                            <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">
                              {(phrasePage - 1) * PAGE_SIZE + i + 1}
                            </TableCell>
                            <TableCell className="py-4 text-base font-medium text-gray-900">{entry.phrase}</TableCell>
                            <TableCell className="py-4">
                              <div className="flex justify-center">
                                <button
                                  type="button"
                                  onClick={() => deletePhraseMutation.mutate(entry.id)}
                                  disabled={deletePhraseMutation.isPending}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                                  aria-label="削除"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  {filteredPhraseEntries.length > PAGE_SIZE && (
                    <div className="flex justify-between items-center mt-5 pt-4">
                      <div className="text-base text-gray-700">
                        全 {filteredPhraseEntries.length} 件中 {((phrasePage - 1) * PAGE_SIZE + 1)} - {Math.min(phrasePage * PAGE_SIZE, filteredPhraseEntries.length)} 件を表示
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPhrasePage(phrasePage - 1)}
                          disabled={phrasePage === 1}
                          className="h-9 rounded-lg border-gray-200"
                        >
                          前へ
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPhrasePage(phrasePage + 1)}
                          disabled={phrasePage * PAGE_SIZE >= filteredPhraseEntries.length}
                          className="h-9 rounded-lg border-gray-200"
                        >
                          次へ
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
